"use client";

import { useEffect, useId, useRef, type ReactNode } from "react";
import { cx } from "./ui";

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
} as const;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * One dialog for the whole app. Escape to leave, the backdrop to leave, focus
 * moved in on open and handed back on close, and the page behind held still.
 * Every dialog used to reimplement some subset of that and miss the rest.
 */
export function Modal({
  title,
  description,
  onClose,
  size = "md",
  children,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  size?: keyof typeof SIZES;
  children: ReactNode;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }

      // Tab must not walk out into the page behind the dialog.
      if (event.key !== "Tab") return;
      const targets = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!targets || targets.length === 0) return;

      const first = targets[0]!;
      const last = targets[targets.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-[2px] sm:items-center sm:p-4 sm:pb-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cx(
          "flex max-h-[min(680px,90dvh)] w-full flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl shadow-ink/20 max-sm:rounded-b-none",
          SIZES[size],
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line/80 px-5 py-4">
          <div className="min-w-0">
            <h3 id={titleId} className="font-semibold tracking-tight text-ink">
              {title}
            </h3>
            {description ? (
              <p className="mt-0.5 text-sm leading-relaxed text-ink-muted">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mt-1 -mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-ink-subtle transition hover:bg-canvas hover:text-ink"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-4" aria-hidden="true">
      <path
        d="m4.5 4.5 7 7m0-7-7 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
